
import Chip from '@mui/material/Chip'
import CardList from 'components/CardList'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { STATUSES_CATEGORIES } from 'routes/find/routes/statuses/statuses.config'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { recentStatusesSearchesSelector } from 'services/content/selectors'
import {
  addRecentStatusSearch,
  deleteSavedSearch,
  getPastSearches,
  setRecentStatusSearches
} from 'services/content/statuses/actions'
import { StoreThunkDispatch } from 'store/state'
import { CategoryChip } from './CategoryChip'
import { QueryChip } from './QueryChip'
import styles from './SuggestedStatusSearches.pcss'

const categories = STATUSES_CATEGORIES.map(cat => ({ ...cat, name: cat.title }))
const CATEGORY_LIST_HEIGHT = 40

interface SuggestedStatusSearchesProps {
  activeQuery: string
  activeCategoryId?: string
  className?: string
  hideCategories?: boolean
  hideSavedSearches?: boolean
  searchElement?: React.ReactNode
  onQueryClick: (value: string) => void
  onActiveCategoryChange?: (id: string, name: string) => void
}

export function SuggestedStatusSearches(props: SuggestedStatusSearchesProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const deleteSearch$ = React.useRef<Subject<string>>(undefined)
  const recentSearches = useSelector(recentStatusesSearchesSelector)
  const [loading, setLoading] = React.useState(recentSearches.length === 0)

  const arr = new Array(6).fill('') // eslint-disable-line no-magic-numbers
  const loadingChips = arr.map((_, i) => <Chip key={i} className={`${styles['loading-chip']} ${styles.gradient}`} />)

  React.useEffect(() => {
    const sub = dispatch(getPastSearches())
      .pipe(catchError(() => observableOf([])))
      .subscribe(() => {
        setLoading(false)
      })

    return () => {
      sub.unsubscribe()
    }
  }, [dispatch])

  React.useEffect(() => {
    deleteSearch$.current = new Subject()
    deleteSearch$.current
      .pipe(
        mergeMap((text: string) => dispatch(deleteSavedSearch(text))
          .pipe(
            catchError(() => observableOf({ error: true }))
          )
        )
      ).subscribe((response: any) => {
        if (response.error) {
          return
        }

        dispatch(setRecentStatusSearches(recentSearches.filter(t => t !== response)))
      })

    return () => {
      deleteSearch$.current?.unsubscribe()
    }
  }, [recentSearches, dispatch])

  React.useEffect(() => {
    if (props.activeQuery && !props.activeCategoryId) {
      dispatch(addRecentStatusSearch(props.activeQuery))
    }
  }, [props.activeQuery, dispatch, props.activeCategoryId])

  const deleteSearch = (text: string) => {
    deleteSearch$.current?.next(text)
  }

  const onActiveCategoryChange = (id: string, name: string) => {
    if (props.onActiveCategoryChange) {
      props.onActiveCategoryChange(id, name)
    }
  }

  return (
    <div className={props.className || ''}>
      {(!props.hideSavedSearches || props.searchElement) && (
        <div className={styles.list}>
          {props.searchElement}
          {!props.hideSavedSearches && loading && (
            <div className={styles['loading-box']}>
              {loadingChips}
            </div>
          )}
          {!props.hideSavedSearches && !loading && (
            recentSearches.map(q => (
              <QueryChip
                key={q}
                label={q}
                className={styles.chip}
                active={q === props.activeQuery}
                onClick={props.onQueryClick}
                onDelete={deleteSearch}
              />
            ))
          )}
        </div>
      )}
      {!props.hideCategories && (
        <CardList listHeight={CATEGORY_LIST_HEIGHT} containerClassName={styles.carousel}>
          {categories.map(cat => (
            <CategoryChip
              key={cat.id}
              id={cat.id}
              name={cat.name}
              active={props.activeCategoryId === cat.id}
              onClick={onActiveCategoryChange}
            />
          ))}
        </CardList>
      )}
    </div>
  )
}
